import React, { useState, useEffect,useMemo  } from "react";
import ReportLayout, {
  DateRangePicker,
  StatusBadge,
  ProgressBar,
} from "./components/ReportLayout";

import {
  Clock,
  Timer,
  AlertTriangle,
  CheckCircle2,
  Utensils,
  TrendingUp,
  Zap,
  ChefHat,
  Building2,
  ChevronDown,
} from "lucide-react";

import useKOTTimingAnalysis from "@/hooks/useKOTTimingAnalysis";
import kitchenStationService from "../../../services/kitchenStationService";
import { categoryService } from "../../../services/categoryService";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
const PERIOD_MAP = {
  today: "today",
  yesterday: "yesterday",
  thisWeek: "this_week",
  thisMonth: "this_month",
  lastMonth: "last_month",
};

export default function KOTTimingReport() {
  const { hasPermission } = useAuth();

if (!hasPermission(P.REPORTS.OPERATIONS.SCREEN)) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Access Denied
        </h3>
        <p className="text-sm text-slate-500">
          You don't have permission to view this page.
        </p>
      </div>
    </div>
  );
}
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("today");

  const [selectedStation, setSelectedStation] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [kitchenStations, setKitchenStations] = useState([]);
  const [categories, setCategories] = useState([]);

  const [isDirty, setIsDirty] = useState(false);

  // ---------- PAGINATION STATE ----------
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');


  const {
    data,
    summary,
    branchSummary,
    loading,
    error,
    setFilters,
    refresh,
  } = useKOTTimingAnalysis({
    period: PERIOD_MAP.today,
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
  });

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

  useEffect(() => {
    kitchenStationService.getAll().then(res => setKitchenStations(res || []));
    categoryService.getAll().then(res => setCategories(res || []));
  }, []);

  // ----------- TOTAL COUNT FROM API ----------
  useEffect(() => {
    if (summary?.totalItemsPrepared != null) {
      setTotalItems(summary.totalItemsPrepared);
    }
  }, [summary]);

  const handleApply = () => {
    setCurrentPage(1);

    setFilters(prev => ({
      ...prev,
      period: selectedPreset ? PERIOD_MAP[selectedPreset] : null,
      startDate,
      endDate,
      offset: 0,
      limit: pageSize,
      kitchenStationId: selectedStation || null,
      categoryId: selectedCategory || null,
    }));

    refresh();
  };

  const exportToCsv = () => {
    if (!data?.length) return;

    const headers = columns.map(c => `"${c.label}"`).join(',');
    const rows = data.map(r =>
      columns.map(c => `"${r[c.key] ?? ''}"`).join(',')
    );

    const blob = new Blob([headers, '\n', rows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'KOT-Timing-Analysis-report.csv';
    link.click();
  };

  const summaryCards = summary
    ? [
        { title: "Items Prepared", value: summary.totalItemsPrepared ?? 0, icon: Utensils },
        { title: "Avg Prep Time", value: `${(summary.avgPreparationTimeMins ?? summary.avgPrepTimeMins ?? 0).toFixed(1)} min`, icon: Timer },
        { title: "On Time", value: summary.itemsOnTime ?? 0, icon: CheckCircle2 },
        { title: "Delayed", value: summary.itemsDelayed ?? 0, icon: AlertTriangle },
        { title: "On-Time Rate", value: `${(summary.onTimePercentage ?? 0).toFixed(1)}%`, icon: TrendingUp },
        { title: "Fastest Item", value: summary.fastestItem ?? '—', icon: Zap },
      ]
    : [];

  const columns = [
    {
      key: "itemName",
      label: "Item",
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{value}</p>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={row.categoryName} type="info" />
            <span className="text-xs text-gray-400">
              {row.kitchenStation}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "expectedTimeMins",
      label: "Target",
      sortable: true,
      align: "center",
      render: (value) => <span>{value} min</span>,
    },
    {
      key: "avgActualTimeMins",
      label: "Actual Avg",
      sortable: true,
      align: "center",
      render: (value, row) => {
        const isOver = value > row.expectedTimeMins;
        return (
          <span
            className={`font-semibold ${
              isOver ? "text-red-600" : "text-green-600"
            }`}
          >
            {value?.toFixed(1)} min
          </span>
        );
      },
    },
    {
      key: "minTimeMins",
      label: "Min",
      sortable: true,
      align: "center",
      render: (value) => (
        <span className="text-green-600">{value} min</span>
      ),
    },
    {
      key: "maxTimeMins",
      label: "Max",
      sortable: true,
      align: "center",
      render: (value) => (
        <span className="text-red-600">{value} min</span>
      ),
    },
    {
      key: "totalOrdered",
      label: "Orders",
      sortable: true,
      align: "center",
      render: (value) => (
        <span className="font-semibold text-gray-900">{value}</span>
      ),
    },
    {
      key: "onTimeCount",
      label: "On Time",
      sortable: true,
      align: "center",
      render: (value) => (
        <span className="text-green-600">{value}</span>
      ),
    },
    {
      key: "delayedCount",
      label: "Delayed",
      sortable: true,
      align: "center",
      render: (value) =>
        value > 0 ? (
          <span className="text-red-600">{value}</span>
        ) : (
          <span className="text-gray-300">-</span>
        ),
    },
    {
      key: "onTimePercentage",
      label: "Performance",
      sortable: true,
      width: "140px",
      render: (value) => (
        <div className="w-28">
          <ProgressBar
            value={value}
            max={100}
            color={
              value >= 90
                ? "green"
                : value >= 75
                ? "amber"
                : "red"
            }
          />
        </div>
      ),
    },
  ];

  const filters = (
    <div className="space-y-4">

      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={v => { setStartDate(v); setSelectedPreset(""); setIsDirty(true); }}
        onEndDateChange={v => { setEndDate(v); setSelectedPreset(""); setIsDirty(true); }}
        selectedPreset={selectedPreset}
     onPresetSelect={p => {
       setSelectedPreset(p);
       setStartDate("");
       setEndDate("");
       setIsDirty(false);

       setCurrentPage(1);

       setFilters(prev => {
         const newFilters = {
           ...prev,
           period: PERIOD_MAP[p],
           startDate: "",
           endDate: "",
           offset: 0,
           limit: pageSize,
           kitchenStationId: selectedStation || null,
           categoryId: selectedCategory || null,
         };


         refresh(newFilters);

         return newFilters;
       });
     }}


      />

      <div className="flex flex-wrap gap-4 items-end border-t pt-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">
            Kitchen Station
          </label>

          <select
            value={selectedStation}
            onChange={e => {
              setSelectedStation(e.target.value ? Number(e.target.value) : "");
              setSelectedPreset("");
              setIsDirty(true);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg min-w-[180px]
                       focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="">All</option>
            {kitchenStations.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">
            Category
          </label>

          <select
            value={selectedCategory}
            onChange={e => {
              setSelectedCategory(e.target.value ? Number(e.target.value) : "");
              setSelectedPreset("");
              setIsDirty(true);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg min-w-[180px]
                       focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="">All</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-transparent">
            Apply
          </label>

          <button
            onClick={handleApply}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            Apply Filters
          </button>
        </div>

        {branchOptions.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Branch</label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={selectedBranchId ?? ''}
                onChange={(e) => {
                  const val = e.target.value || null;
                  setSelectedBranchId(val);
                  setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
                }}
                className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
              >
                <option value="">All Branches</option>
                {branchOptions.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
 //SORTING
  const sortedData = useMemo(() => {
  if (!data || !sortColumn) return data;

  const sorted = [...data];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    }

    return sortDirection === 'asc'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : String(bVal ?? '').localeCompare(String(aVal ?? ''));
  });

  return sorted;
}, [data, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;
  const query = searchQuery.toLowerCase().trim();
  return sortedData.filter(row => {
    // Search in item name
    if (String(row.itemName || '').toLowerCase().includes(query)) return true;
    // Search in category name
    if (String(row.categoryName || '').toLowerCase().includes(query)) return true;
    // Search in kitchen station
    if (String(row.kitchenStation || '').toLowerCase().includes(query)) return true;
    // Search in numeric fields
    const numericFields = ['expectedTimeMins', 'avgActualTimeMins', 'minTimeMins', 'maxTimeMins', 'totalOrdered', 'onTimeCount', 'delayedCount', 'onTimePercentage'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
      }
    }
    return false;
  });
}, [sortedData, searchQuery]);


  return (
    <ReportLayout
      title="KOT Timing Analysis"
      description="Identify kitchen bottlenecks and slow-preparing items"
      icon={Clock}
      exportPermission={P.REPORTS.OPERATIONS.KOT_EXPORT}
      color="purple"
      category="Operations"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filters}
      loading={loading}
      error={error}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={(column) => {
      let nextDirection;

      if (sortColumn === column) {
        nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        nextDirection = 'asc';
      }
      setSortColumn(column);
      setSortDirection(nextDirection);
    }}

      onRefresh={refresh}
      onExport={(format) => {
        if (format === 'csv') exportToCsv();
      }}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by item, category, station..."
      pagination={{
        currentPage,
        totalPages: Math.ceil(totalItems / pageSize),
        totalItems,
        pageSize,
        onPageChange: (page) => {
          setCurrentPage(page);
          setFilters(prev => ({
            ...prev,
            offset: (page - 1) * pageSize,
            limit: pageSize,
          }));
          refresh();
        },
        onPageSizeChange: (size) => {
          setPageSize(size);
          setCurrentPage(1);
          setFilters(prev => ({
            ...prev,
            offset: 0,
            limit: size,
          }));
          refresh();
        }
      }}
    >

      {/* ---- SAME UI BELOW ---- */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
              <AlertTriangle size={16} className="text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold">Bottlenecks</h3>
              <p className="text-xs text-gray-500">Items often delayed</p>
            </div>
          </div>

          <div className="space-y-2">
            {[...(data || [])]
              .sort((a, b) => a.onTimePercentage - b.onTimePercentage)
              .slice(0, 3)
              .map(item => (
                <div key={item.itemId} className="flex justify-between bg-red-50 p-2 rounded">
                  <div>
                    <p className="text-sm font-medium">{item.itemName}</p>
                    <p className="text-xs text-gray-500">{item.delayedCount} delays</p>
                  </div>
                  <span className="text-red-600 font-semibold">
                    {item.onTimePercentage.toFixed(0)}%
                  </span>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
              <CheckCircle2 size={16} className="text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">Top Performers</h3>
              <p className="text-xs text-gray-500">Most consistent</p>
            </div>
          </div>

          <div className="space-y-2">
            {[...(data || [])]
              .sort((a, b) => b.onTimePercentage - a.onTimePercentage)
              .slice(0, 3)
              .map(item => (
                <div key={item.itemId} className="flex justify-between bg-green-50 p-2 rounded">
                  <div>
                    <p className="text-sm font-medium">{item.itemName}</p>
                    <p className="text-xs">{item.avgActualTimeMins?.toFixed(1)} min avg</p>
                  </div>
                  <span className="text-green-600 font-semibold">
                    {item.onTimePercentage.toFixed(0)}%
                  </span>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
              <ChefHat size={16} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">By Station</h3>
              <p className="text-xs text-gray-500">Performance breakdown</p>
            </div>
          </div>

          <div className="space-y-3">
            {["Main Kitchen","Tandoor Station","Dessert Station","Beverage Station"]
              .map(station => {

                const stationData = (data || []).filter(
                  i => i.kitchenStation === station
                );

                const total = stationData.reduce((s,i)=>s+i.totalOrdered,0);
                const onTime = stationData.reduce((s,i)=>s+i.onTimeCount,0);

                const rate = total ? (onTime/total)*100 : 0;

                return (
                  <div key={station} className="p-2 bg-gray-50 rounded">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{station}</span>
                      <span className={rate>=90?"text-green-600"
                        :rate>=75?"text-amber-600":"text-red-600"}>
                        {rate.toFixed(0)}%
                      </span>
                    </div>
                    <ProgressBar value={rate} max={100} showLabel={false}/>
                  </div>
                );
              })}
          </div>
        </div>

      </div>

    </ReportLayout>
  );
}
